import { generatePath } from 'react-router';

import { findTenantBySlug, resolveActiveTenantSlug } from './tenant-routing';

export {
  aliasLegacyTenantSlug,
  buildTenantRootPath,
  CANONICAL_TI_COMMUNITIES_SLUG,
  LEGACY_TI_COMMUNITIES_SLUG,
  readPropertyIdFromCustomerPath,
  readTenantSlugFromCurrentUrl,
  readTenantSlugFromPath,
  readTenantSuffixFromPath,
  TENANT_PATH_PREFIX,
  TENANT_ROOT,
  TENANT_SCOPE,
  TENANT_SLUG_PARAM,
} from './tenant-path';

import {
  aliasLegacyTenantSlug,
  buildTenantRootPath,
  readTenantSlugFromCurrentUrl,
  readTenantSuffixFromPath,
  TENANT_ROOT,
  TENANT_SLUG_PARAM,
} from './tenant-path';

const TENANT_ROOT_PREFIX = `${TENANT_ROOT}/`;

/**
 * Strips the `/~/:tenantSlug/` prefix from a full route template so it can be
 * used as a relative child path under the tenant route in `routes.tsx`.
 */
export function tenantChildPath(fullTemplate: string): string {
  if (fullTemplate.startsWith(TENANT_ROOT_PREFIX)) {
    return fullTemplate.slice(TENANT_ROOT_PREFIX.length);
  }
  if (fullTemplate === TENANT_ROOT) {
    return '';
  }
  return fullTemplate;
}

const ADMIN_ROOT_PREFIX = `${TENANT_ROOT}/admin/` as const;

/**
 * Strips the `/~/:tenantSlug/admin/` prefix from a full route template so it can
 * be used as a relative child path under the admin route in `routes.tsx`.
 */
export function adminChildPath(fullTemplate: string): string {
  if (fullTemplate.startsWith(ADMIN_ROOT_PREFIX)) {
    return fullTemplate.slice(ADMIN_ROOT_PREFIX.length);
  }
  if (fullTemplate === `${TENANT_ROOT}/admin`) {
    return '';
  }
  return fullTemplate;
}

const SURFOPS_ROOT_PREFIX = `${TENANT_ROOT}/surfops/` as const;

/**
 * Strips the `/~/:tenantSlug/surfops/` prefix from a full route template so it
 * can be used as a relative child path under the surfops route in `routes.tsx`.
 */
export function surfopsChildPath(fullTemplate: string): string {
  if (fullTemplate.startsWith(SURFOPS_ROOT_PREFIX)) {
    return fullTemplate.slice(SURFOPS_ROOT_PREFIX.length);
  }
  if (fullTemplate === `${TENANT_ROOT}/surfops`) {
    return '';
  }
  return fullTemplate;
}

/** True when `pathname` is a SurfOps route under `/~/:tenantSlug/surfops` or legacy `/surfops`. */
export function isSurfOpsPath(pathname: string): boolean {
  if (!pathname || typeof pathname !== 'string') return false;
  const suffix = readTenantSuffixFromPath(pathname);
  if (suffix) {
    return suffix === '/surfops' || suffix.startsWith('/surfops/');
  }
  return pathname === '/surfops' || pathname.startsWith('/surfops/');
}

/**
 * True when `pathname` is a Settings admin route (`/~/:tenantSlug/admin/...`)
 * or a legacy tenantless `/admin/...` bookmark pending redirect.
 */
export function isAdminSettingsPath(pathname: string): boolean {
  if (!pathname || typeof pathname !== 'string') return false;
  const suffix = readTenantSuffixFromPath(pathname);
  if (suffix) {
    return suffix === '/admin' || suffix.startsWith('/admin/');
  }
  return pathname === '/admin' || pathname.startsWith('/admin/');
}

/** List-level paths portable when switching tenants (no entity ids). */
const PORTABLE_SUFFIXES = [
  '/lease-audits',
  '/ask',
  '/due-diligence',
  '/documents',
  '/document-management',
  '/property-transitions',
  '/issues-reported',
  '/priorities',
  '/help',
  '/admin',
  '/surfops',
] as const;

/**
 * Tenantless path prefixes rewritten by LegacyTenantRedirect to
 * /~/{slug}/…. Includes portable list pages plus /properties
 * for entity deep links (not portable across tenant switches).
 */
export const LEGACY_CUSTOMER_PREFIXES = [
  ...PORTABLE_SUFFIXES,
  '/properties',
  '/surfops',
] as const;

/** Low-cardinality label for analytics when a legacy tenantless URL is hit. */
export function legacyCustomerPathLabel(pathname: string): string {
  if (!pathname || typeof pathname !== 'string') return '/';
  const matched = LEGACY_CUSTOMER_PREFIXES.find(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
  if (matched) return matched;
  const segment = pathname.split('/').filter(Boolean)[0];
  return segment ? `/${segment}` : '/';
}

/** True when `pathname` is a known customer area that predates `/~/:tenantSlug`. */
export function isLegacyCustomerPath(pathname: string): boolean {
  if (!pathname || typeof pathname !== 'string') return false;
  return LEGACY_CUSTOMER_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

/** Exported for tests — list-level suffixes portable when switching tenants. */
export function isPortableTenantSuffix(suffix: string): boolean {
  if (suffix === '/') return true;
  // Property-scoped entity paths (e.g. /properties/:id/lease-audits/:auditId)
  // are never portable even when they contain a portable segment like /lease-audits.
  if (suffix.startsWith('/properties/')) return false;
  return PORTABLE_SUFFIXES.some(
    (portable) => suffix === portable || suffix.startsWith(`${portable}/`)
  );
}

/**
 * Builds the navigation target when switching to `nextTenantSlug`.
 *
 * - Authenticated globals (e.g. `/whats-new`) stay on the same path.
 * - Other global routes (no tenant in URL) → `/~/{slug}` (default landing).
 * - Portable list pages → same suffix under the new tenant.
 * - Entity-detail paths → tenant root (entity ids are not portable).
 */
export function buildPathForTenant(
  pathname: string,
  nextTenantSlug: string,
  search = '',
  hash = ''
): string {
  if (isGlobalAuthenticatedPath(pathname)) {
    return `${pathname}${search}${hash}`;
  }

  const suffix = readTenantSuffixFromPath(pathname);
  const base = buildTenantRootPath(nextTenantSlug);

  if (!suffix) {
    return `${base}${search}${hash}`;
  }

  if (isPortableTenantSuffix(suffix)) {
    const path = suffix === '/' ? base : `${base}${suffix}`;
    return `${path}${search}${hash}`;
  }

  // Entity-detail drop: land on tenant root without entity-specific query/hash.
  return base;
}

/**
 * Resolves a tenant slug for `getPath.*` materialization. Explicit slug args are
 * validated against `setTenantRoutingContext` cache. Callers that already
 * resolved a slug via tenant-routing policy should use `buildTenantRootPath`
 * instead of routing through `getPath.tenantRoot`.
 *
 * When omitted, reads the slug from the current URL, then the session tenant
 * (for authenticated globals such as `/whats-new`). Falls back to the literal
 * `:tenantSlug` placeholder when neither is available.
 */
function resolveTenantSlug(tenantSlug?: string | null): string {
  if (tenantSlug) {
    const match = findTenantBySlug(tenantSlug);
    if (match?.slug) return aliasLegacyTenantSlug(match.slug);
    return TENANT_SLUG_PARAM;
  }
  const urlTenantSlug = readTenantSlugFromCurrentUrl();
  if (urlTenantSlug) {
    return urlTenantSlug;
  }
  return resolveActiveTenantSlug() ?? TENANT_SLUG_PARAM;
}

// Root
export const ROOT = '/' as const;

// Ask
export const ASK = `${TENANT_ROOT}/ask` as const;
export const DIALOGUE_DETAILS = `${ASK}/question/:dialogueId` as const;

// Help
export const HELP = `${TENANT_ROOT}/help` as const;
export const HELP_LEASE_AUDITS = `${HELP}/lease-audits` as const;
export const HELP_BROWSER_EXTENSION = `${HELP}/browser-extension` as const;

export const WHATS_NEW = `${ROOT}whats-new` as const;

/**
 * Resources
 * -----------------
 * Resources are the entities that exist within the application.
 * These are the entities that are used to generate paths.
 */
export const DELINQUENCIES_RESOURCE = 'delinquencies';

/**
/**
 * Priorities
 * -----------------
 * Task views that exist within the priorities context.
 * These are filterable tables of tasks that can exist across multiple properties.
 *
 */
export const PRIORITIES = `${TENANT_ROOT}/priorities` as const;

export const APPLICATIONS = `${PRIORITIES}/applications` as const;
export const MOVE_INS = `${PRIORITIES}/move-ins` as const;
export const RENEWALS = `${PRIORITIES}/renewals` as const;
export const MOVE_OUTS = `${PRIORITIES}/move-outs` as const;
export const SERVICE_REQUESTS = `${PRIORITIES}/service-requests` as const;
export const LEASE_AUDITS = `${TENANT_ROOT}/lease-audits` as const;
export const LEASE_AUDIT_HOT_SPOT_DETAIL =
  `${LEASE_AUDITS}/hotspots/:checkType/:failureType` as const;
export const LEASE_AUDIT_HOT_SPOT_CHECK_EXPLORER =
  `${LEASE_AUDIT_HOT_SPOT_DETAIL}/check-explorer/:propertyId/:auditId/:auditRunId` as const;
export const DELINQUENCIES = `${PRIORITIES}/${DELINQUENCIES_RESOURCE}` as const;
export const DUE_DILIGENCES = `${TENANT_ROOT}/due-diligence` as const;
export const DUE_DILIGENCE_NEW = `${DUE_DILIGENCES}/new` as const;
export const DUE_DILIGENCE_REPORT = `${DUE_DILIGENCES}/report` as const;
export const DUE_DILIGENCE_PDF_VIEW = `${DUE_DILIGENCES}/pdf-view` as const;
export const DUE_DILIGENCE_REPORTING_SETTINGS =
  `${DUE_DILIGENCES}/reporting-settings` as const;

/**
 * Properties
 * -----------------
 * Entity detail page views that exist within the property context.
 */

export const PROPERTIES = `${TENANT_ROOT}/properties` as const;
export const PROPERTY = `${PROPERTIES}/:propertyId` as const;
export const TASK_DETAILS = `${PROPERTY}/tasks/:taskId` as const;
export const DELINQUENCY =
  `${PROPERTY}/${DELINQUENCIES_RESOURCE}/:delinquencyId` as const;

export const LEASE_AUDIT = `${PROPERTY}/lease-audits/:auditId` as const;
export const LEASE_DOCUMENTS = `${PROPERTY}/leases/:leaseId/documents` as const;
export const DOCUMENT_DETAILS = `${PROPERTY}/documents/:documentId` as const;
export const DOCUMENT_PROFILE_EDITOR =
  `${DOCUMENT_DETAILS}/profile_raw` as const;
export const DOCUMENT_EXTRACT_EDITOR =
  `${DOCUMENT_DETAILS}/extract_raw` as const;
export const DOCUMENT_INDEX_EDITOR = `${DOCUMENT_DETAILS}/index` as const;
export const LEASE_AUDIT_RUNS = `${LEASE_AUDIT}/runs` as const;
export const LEASE_AUDIT_RUN = `${LEASE_AUDIT_RUNS}/:auditRunId` as const;
export const LEASE_AUDIT_CHECK_EXPLORER =
  `${LEASE_AUDIT_RUN}/check-explorer/:checkType` as const;

export const DUE_DILIGENCE = `${PROPERTY}/due-diligence` as const;
export const DUE_DILIGENCE_AUDIT = `${DUE_DILIGENCE}/:auditId` as const;
export const DUE_DILIGENCE_AUDIT_RUNS = `${DUE_DILIGENCE_AUDIT}/runs` as const;
export const DUE_DILIGENCE_AUDIT_RUN =
  `${DUE_DILIGENCE_AUDIT_RUNS}/:auditRunId` as const;
export const DUE_DILIGENCE_AUDIT_CHECK_EXPLORER =
  `${DUE_DILIGENCE_AUDIT_RUN}/check-explorer/:checkType` as const;

// Due Diligence: property workflows (moved from admin prospective)
export const DUE_DILIGENCE_PROPERTY = `${DUE_DILIGENCES}/:propertyId` as const;
export const DUE_DILIGENCE_PROPERTY_LATEST =
  `${DUE_DILIGENCE_PROPERTY}/latest` as const;
export const DUE_DILIGENCE_PROPERTY_CHARGE_CODES =
  `${DUE_DILIGENCE_PROPERTY}/charge-codes` as const;
export const DUE_DILIGENCE_PROPERTY_SETUP =
  `${DUE_DILIGENCE_PROPERTY}/setup` as const;
export const DUE_DILIGENCE_PROPERTY_CONFIGURE_CHECKS =
  `${DUE_DILIGENCE_PROPERTY}/configure-checks` as const;
export const DUE_DILIGENCE_PROPERTY_DOCUMENTS =
  `${DUE_DILIGENCE_PROPERTY}/documents` as const;
export const DUE_DILIGENCE_PROPERTY_LEASE_SUMMARY =
  `${DUE_DILIGENCE_PROPERTY}/lease-summary` as const;
export const DUE_DILIGENCE_PROPERTY_PROCESSING =
  `${DUE_DILIGENCE_PROPERTY}/processing` as const;
export const DUE_DILIGENCE_PROPERTY_DOCUMENT_CLASSIFICATION =
  `${DUE_DILIGENCE_PROPERTY}/document-classification` as const;

// Admin (Settings — lives under /~/:tenantSlug/admin)
export const ADMIN = `${TENANT_ROOT}/admin` as const;
export const ADMIN_TENANTS = `${ADMIN}/tenants` as const;
export const ADMIN_USERS = `${ADMIN}/users` as const;
export const ADMIN_PROPERTIES = `${ADMIN}/properties` as const;

// Admin: Prospective
export const ADMIN_PROPERTIES_NEW =
  `${ADMIN_PROPERTIES}/prospective/new` as const;
export const ADMIN_PROSPECTIVE_PROPERTY =
  `${ADMIN_PROPERTIES}/prospective/:propertyId` as const;
export const ADMIN_PROSPECTIVE_PROPERTY_CHARGE_CODES =
  `${ADMIN_PROSPECTIVE_PROPERTY}/charge-codes` as const;
export const ADMIN_PROSPECTIVE_PROPERTY_SETUP =
  `${ADMIN_PROSPECTIVE_PROPERTY}/setup` as const;
export const ADMIN_PROSPECTIVE_PROPERTY_CONFIGURE_CHECKS =
  `${ADMIN_PROSPECTIVE_PROPERTY}/configure-checks` as const;
export const ADMIN_PROSPECTIVE_PROPERTY_DOCUMENTS =
  `${ADMIN_PROSPECTIVE_PROPERTY}/documents` as const;
export const ADMIN_PROSPECTIVE_PROPERTY_LEASE_SUMMARY =
  `${ADMIN_PROSPECTIVE_PROPERTY}/lease-summary` as const;
export const ADMIN_PROSPECTIVE_PROPERTY_PROCESSING =
  `${ADMIN_PROSPECTIVE_PROPERTY}/processing` as const;
export const ADMIN_PROSPECTIVE_PROPERTY_DOCUMENT_CLASSIFICATION =
  `${ADMIN_PROSPECTIVE_PROPERTY}/document-classification` as const;

// Admin: Managed property routes
export const ADMIN_MANAGED_PROPERTY =
  `${ADMIN_PROPERTIES}/managed/:propertyId` as const;
export const ADMIN_PROPERTY_MANAGED_CHARGE_CODES =
  `${ADMIN_MANAGED_PROPERTY}/charge-codes` as const;
export const ADMIN_PROPERTY_MANAGED_CHECKS =
  `${ADMIN_MANAGED_PROPERTY}/checks` as const;

// Download
export const DOWNLOAD = `${ROOT}download` as const;

// Login
export const LOGIN = `${ROOT}login` as const;
export const LOGIN_MAGIC_LINK_VERIFY = `${LOGIN}/magic-link/verify` as const;
export const LOGIN_SUCCESS = `${ROOT}login-success` as const;
export const SENTRY = `${ROOT}sentry` as const;

/**
 * Authenticated routes that live outside `/~/:tenantSlug` (product-wide, not
 * tenant-scoped). Tenant switching stays on these paths; post-login redirects
 * must not prefix them with `/~/{slug}`.
 */
export function isGlobalAuthenticatedPath(pathname: string): boolean {
  if (!pathname || typeof pathname !== 'string') return false;
  return (
    pathname === WHATS_NEW ||
    pathname.startsWith(`${WHATS_NEW}/`) ||
    pathname === LOGIN_SUCCESS ||
    pathname === SENTRY ||
    pathname.startsWith(`${SENTRY}/`)
  );
}

// Onboarding
export const NEW_USER = `${ROOT}new-user` as const;
export const USER_ONBOARDING = `${ROOT}user-onboarding` as const;

// Documents
export const DOCUMENTS = `${TENANT_ROOT}/documents` as const;
export const DOCUMENT_MANAGEMENT =
  `${TENANT_ROOT}/document-management` as const;
export const DOCUMENT_MANAGEMENT_NEW = `${DOCUMENT_MANAGEMENT}/new` as const;
export const DOCUMENT_MANAGEMENT_DETAIL =
  `${DOCUMENT_MANAGEMENT}/:propertyId/:id` as const;
export const DOCUMENT_MANAGEMENT_UPLOAD =
  `${DOCUMENT_MANAGEMENT}/:propertyId/:id/upload` as const;
export const DOCUMENT_MANAGEMENT_PROCESSING =
  `${DOCUMENT_MANAGEMENT}/:propertyId/:id/processing` as const;
export const DOCUMENT_MANAGEMENT_REVIEW =
  `${DOCUMENT_MANAGEMENT}/:propertyId/:id/review` as const;

// Property transitions
export const PROPERTY_TRANSITIONS =
  `${TENANT_ROOT}/property-transitions` as const;
export const PROPERTY_TRANSITIONS_CREATE =
  `${PROPERTY_TRANSITIONS}/create` as const;
/**
 * List of transition templates for the current tenant. Templates are ``Transition``
 * rows with ``is_template=True`` and are edited via the standard transition detail
 * page (which renders a template-restricted UI when the target is a template).
 */
export const PROPERTY_TRANSITIONS_TEMPLATES =
  `${PROPERTY_TRANSITIONS}/templates` as const;
export const PROPERTY_TRANSITIONS_DETAIL =
  `${PROPERTY_TRANSITIONS}/:transitionId` as const;
/** Post-create setup wizard (milestones, tasks, collections). */
export const PROPERTY_TRANSITIONS_SETUP =
  `${PROPERTY_TRANSITIONS_DETAIL}/setup` as const;
/** Transition-level document viewer. */
export const PROPERTY_TRANSITIONS_DOCUMENT =
  `${PROPERTY_TRANSITIONS}/:transitionId/documents/:documentId` as const;
/** Single document task within a transition. */
export const PROPERTY_TRANSITIONS_TASK =
  `${PROPERTY_TRANSITIONS}/:transitionId/tasks/:transitionTaskId` as const;
/** Create a new document task within a transition. */
export const PROPERTY_TRANSITIONS_TASK_CREATE =
  `${PROPERTY_TRANSITIONS}/:transitionId/tasks/create` as const;
/** A matched document row scoped to a document task. */
export const PROPERTY_TRANSITIONS_TASK_DOCUMENT =
  `${PROPERTY_TRANSITIONS_TASK}/documents/:documentId` as const;
/** Legacy edit-details URL; redirects to task detail for bookmarks. */
export const PROPERTY_TRANSITIONS_TASK_EDIT_DETAILS =
  `${PROPERTY_TRANSITIONS_TASK}/edit/details` as const;
/** Edit task document requirements and matching rules. */
export const PROPERTY_TRANSITIONS_TASK_EDIT_DOCUMENT_REQUIREMENTS =
  `${PROPERTY_TRANSITIONS_TASK}/edit/document-requirements` as const;
/** Transition variables — values and template bindings for a transition. */
export const PROPERTY_TRANSITIONS_DETAIL_VARIABLES =
  `${PROPERTY_TRANSITIONS_DETAIL}/variables` as const;
/** Manage document collections for a transition. */
export const PROPERTY_TRANSITIONS_COLLECTIONS =
  `${PROPERTY_TRANSITIONS}/:transitionId/collections` as const;
/** Single document collection within a transition. */
export const PROPERTY_TRANSITIONS_COLLECTION_DETAIL =
  `${PROPERTY_TRANSITIONS_COLLECTIONS}/:collectionId` as const;
/**
 * Companies management landing page (grid of Company rows for the current
 * tenant). Gated by ``Transitions_Company`` read permission on the route.
 */
export const PROPERTY_TRANSITIONS_COMPANIES =
  `${PROPERTY_TRANSITIONS}/companies` as const;
/** Edit a single company (attributes + teams grid). */
export const PROPERTY_TRANSITIONS_COMPANY_DETAIL =
  `${PROPERTY_TRANSITIONS_COMPANIES}/:companyId` as const;
/**
 * Manage members of one company team. Auto-saves (no explicit save button):
 * add / remove fires the mutation immediately. Team names are open-ended
 * strings scoped to ``(saas_tenant_id, company_id)`` per the backend model
 * (there is no ``team`` DB table).
 */
export const PROPERTY_TRANSITIONS_COMPANY_TEAM =
  `${PROPERTY_TRANSITIONS_COMPANY_DETAIL}/teams/:team` as const;

// Issues Reported (Public)
export const ISSUES_REPORTED = `${TENANT_ROOT}/issues-reported` as const;
export const ISSUES_REPORTED_DETAIL =
  `${ISSUES_REPORTED}/:propertyId/issues/:issueId` as const;

// SurfOps (admin tooling; tenant-scoped URL for API header + tenant combobox)
export const SURFOPS = `${TENANT_ROOT}/surfops` as const;
export const SURFOPS_OPERATIONAL_INTEGRITY =
  `${SURFOPS}/operational-integrity` as const;
export const SURFOPS_CONFIG_MANAGEMENT =
  `${SURFOPS}/config-management` as const;
export const SURFOPS_CONFIG_CREATE =
  `${SURFOPS_CONFIG_MANAGEMENT}/create` as const;
export const SURFOPS_CONFIG_DETAIL =
  `${SURFOPS_CONFIG_MANAGEMENT}/:propertyId/:configType` as const;
export const SURFOPS_CONFIG_HISTORY_MANAGEMENT =
  `${SURFOPS_CONFIG_MANAGEMENT}/versions` as const;
export const SURFOPS_EXPERIMENTS = `${SURFOPS}/experiments` as const;
export const SURFOPS_EXPERIMENTS_CREATE =
  `${SURFOPS_EXPERIMENTS}/create` as const;
export const SURFOPS_EXPERIMENTS_DOCUMENTS =
  `${SURFOPS_EXPERIMENTS}/documents` as const;
export const SURFOPS_EXPERIMENT =
  `${SURFOPS_EXPERIMENTS}/:experimentId` as const;
export const SURFOPS_EXPERIMENT_RUN =
  `${SURFOPS_EXPERIMENT}/runs/:runId` as const;
export const SURFOPS_LEASE_AUDIT_BATCHES =
  `${SURFOPS}/lease-audits/batches` as const;
export const SURFOPS_LEASE_AUDIT_BATCH_CREATE =
  `${SURFOPS_LEASE_AUDIT_BATCHES}/create` as const;
export const SURFOPS_LEASE_AUDIT_BATCH_DETAILS =
  `${SURFOPS_LEASE_AUDIT_BATCHES}/:runId` as const;
export const SURFOPS_LEASE_AUDIT_MEDIC =
  `${SURFOPS}/lease-audit-medic` as const;
export const SURFOPS_LEASE_AUDIT_PROPERTY_ONBOARDING =
  `${SURFOPS}/lease-audit-property-onboarding` as const;
export const SURFOPS_LEASE_AUDIT_PROPERTY_ONBOARDING_DETAIL =
  `${SURFOPS_LEASE_AUDIT_PROPERTY_ONBOARDING}/:runId` as const;
export const SURFOPS_TRIAL_AUDITS = `${SURFOPS}/trial-audits` as const;
export const SURFOPS_TRIAL_AUDIT_BATCH =
  `${SURFOPS_TRIAL_AUDITS}/:batchId` as const;
export const SURFOPS_TRIAL_AUDIT_CHECK =
  `${SURFOPS_TRIAL_AUDIT_BATCH}/checks/:checkId` as const;
export const SURFOPS_EXTRACTION_QUALITY =
  `${SURFOPS}/extraction-quality` as const;
export const SURFOPS_EXTRACTION_QUALITY_CLUSTER =
  `${SURFOPS_EXTRACTION_QUALITY}/clusters/:archetypeId` as const;
export const SURFOPS_EXTRACTION_QUALITY_FINDING =
  `${SURFOPS_EXTRACTION_QUALITY}/:findingId` as const;
export const SURFOPS_EVAL = `${SURFOPS}/eval` as const;
export const SURFOPS_EVAL_CREATE = `${SURFOPS_EVAL}/create` as const;
export const SURFOPS_EVAL_DATASET = `${SURFOPS_EVAL}/:datasetId` as const;
export const SURFOPS_EVAL_LABEL =
  `${SURFOPS_EVAL_DATASET}/label/:itemId` as const;
export const SURFOPS_EVAL_RUN = `${SURFOPS_EVAL_DATASET}/runs/:runId` as const;
export const SURFOPS_EVAL_COMPARE =
  `${SURFOPS_EVAL_DATASET}/runs/compare` as const;
export const SURFOPS_ISSUES_REPORTED = `${SURFOPS}/issues-reported` as const;
export const SURFOPS_ISSUES_REPORTED_DETAILS =
  `${SURFOPS_ISSUES_REPORTED}/:propertyId/issues/:issueId` as const;
export const SURFOPS_API_KEYS = `${SURFOPS}/api-keys` as const;
export const SURFOPS_TENANTS = `${SURFOPS}/tenants` as const;
export const SURFOPS_TENANTS_CREATE = `${SURFOPS_TENANTS}/create` as const;
export const SURFOPS_TENANT = `${SURFOPS_TENANTS}/:surfopsTenantId` as const;
export const SURFOPS_PROPERTIES = `${SURFOPS}/properties` as const;
export const SURFOPS_PROPERTY = `${SURFOPS_PROPERTIES}/:propertyId` as const;
export const SURFOPS_POLICY_DOCUMENTS = `${SURFOPS}/policy-documents` as const;
export const SURFOPS_CLOUDSYNC = `${SURFOPS}/cloudsync` as const;
export const SURFOPS_DOCUMENT_CONTENT_SYNC =
  `${SURFOPS}/document-content-sync` as const;
export const SURFOPS_DOCUMENT_REINDEX = `${SURFOPS}/document-reindex` as const;
export const SURFOPS_AGENT_CHAT = `${SURFOPS}/agent-chat` as const;
export const SURFOPS_PIPELINES = `${SURFOPS}/pipelines` as const;
export const SURFOPS_PIPELINE_RUN =
  `${SURFOPS_PIPELINES}/:pipelineDirectiveId` as const;
export const SURFOPS_EXTERNAL_JOBS = `${SURFOPS}/external-jobs` as const;
/**
 * Every external job for one document id, plus the batches they were pooled into and
 * the lease audit runs that evaluated it. Blind-linkable: a document with no external
 * jobs (or whose jobs have aged out) renders empty rather than 404ing, so callers that
 * only hold a document id — the lease audit debug page, for one — can link here without
 * checking first. Declared with two segments so it never collides with
 * ``SURFOPS_EXTERNAL_JOB``'s single ``:externalJobId``.
 */
export const SURFOPS_EXTERNAL_JOB_DOCUMENT =
  `${SURFOPS_EXTERNAL_JOBS}/by-document/:documentId` as const;
export const SURFOPS_EXTERNAL_JOB =
  `${SURFOPS_EXTERNAL_JOBS}/:externalJobId` as const;
export const SURFOPS_EMAIL_OUTBOX = `${SURFOPS}/email-outbox` as const;
// SurfOps > Email Sync (admin visibility into fetch pipeline sync state
// + poison-pill queue for the ingest pipeline). ``list`` is the address
// index; ``userDetail`` drills into one user's folder sync states; the
// poison-pill queue lives at its own URL so a filter can be bookmarked.
export const SURFOPS_EMAIL_SYNC = `${SURFOPS}/email-sync` as const;
export const SURFOPS_EMAIL_SYNC_USER_DETAIL =
  `${SURFOPS}/email-sync/users/:surfaceUserId` as const;
export const SURFOPS_EMAIL_SYNC_POISON_PILLS =
  `${SURFOPS}/email-sync/poison-pills` as const;
export const SURFOPS_DESIGN = `${SURFOPS}/design` as const;
export const SURFOPS_COMPONENTS = `${SURFOPS}/components` as const;
export const SURFOPS_NOTIFICATIONS = `${SURFOPS}/notifications` as const;
// SurfOps > Transition Doc Request Agent Settings (per-tenant TRANSITION_DOC_AGENT config).
// One page (edit form + inline version history) so no ":versionId" child --
// selecting a version rehydrates the form in read-only mode via ?version=.
export const SURFOPS_TRANSITION_AGENT_SETTINGS =
  `${SURFOPS}/transition-agent-settings` as const;

// SurfOps > Classification Setup (per-tenant intake notes for transition doc classifications).
// Classification value carries slashes/parens etc, so encode when generating.
export const SURFOPS_CLASSIFICATION_SETUP =
  `${SURFOPS}/classification-setup` as const;
export const SURFOPS_CLASSIFICATION_SETUP_DETAIL =
  `${SURFOPS_CLASSIFICATION_SETUP}/:classification` as const;

export const SURFOPS_TRANSITIONS_FILE_UPLOADS =
  `${SURFOPS}/transitions/file-uploads` as const;
export const SURFOPS_TRANSITIONS_FILE_UPLOAD_DETAIL =
  `${SURFOPS_TRANSITIONS_FILE_UPLOADS}/:fileUploadId` as const;
export const SURFOPS_TRANSITIONS_DOCUMENTS =
  `${SURFOPS}/transitions/documents` as const;
export const SURFOPS_TRANSITIONS_DOCUMENT_DETAIL =
  `${SURFOPS}/transitions/:transitionId/documents/:documentId` as const;
export const SURFOPS_TRANSITIONS_DOCUMENT_INDEX_EDITOR =
  `${SURFOPS_TRANSITIONS_DOCUMENT_DETAIL}/index` as const;
export const SURFOPS_TRANSITIONS_DOCUMENT_EXTRACT_EDITOR =
  `${SURFOPS_TRANSITIONS_DOCUMENT_DETAIL}/extract_raw` as const;
export const SURFOPS_TRANSITIONS_DOCUMENT_PROFILE_EDITOR =
  `${SURFOPS_TRANSITIONS_DOCUMENT_DETAIL}/profile_raw` as const;
export const SURFOPS_LLM_COSTS = `${SURFOPS}/llm-costs` as const;

// SurfOps: Orders
export const SURFOPS_ORDERS = `${SURFOPS}/orders` as const;
export const SURFOPS_ORDER_DETAIL = `${SURFOPS_ORDERS}/:orderId` as const;
export const SURFOPS_ORDER_BULK_ADD_ITEMS =
  `${SURFOPS_ORDER_DETAIL}/bulk-add-items` as const;
export const SURFOPS_ORDERS_OUTSTANDING =
  `${SURFOPS_ORDERS}/outstanding` as const;
export const SURFOPS_ORDERS_PROPERTY_SEARCH =
  `${SURFOPS_ORDERS}/property-search` as const;
export const SURFOPS_UNPAID_LICENSED_FEATURES =
  `${SURFOPS}/unpaid-licensed-features` as const;
export const SURFOPS_ORDERS_REPORTS = `${SURFOPS_ORDERS}/reports` as const;
export const SURFOPS_ORDERS_CUSTOMERS = `${SURFOPS_ORDERS}/customers` as const;
export const SURFOPS_ORDERS_CUSTOMER_NEW =
  `${SURFOPS_ORDERS_CUSTOMERS}/new` as const;
export const SURFOPS_ORDERS_CUSTOMER_DETAIL =
  `${SURFOPS_ORDERS_CUSTOMERS}/:customerId` as const;
export const SURFOPS_ORDERS_PREPURCHASES =
  `${SURFOPS_ORDERS}/prepurchases` as const;
export const SURFOPS_ORDERS_PREPURCHASE_DETAIL =
  `${SURFOPS_ORDERS_PREPURCHASES}/:ledgerId` as const;
export const SURFOPS_ORDERS_INVOICES = `${SURFOPS_ORDERS}/invoices` as const;
export const SURFOPS_ORDERS_INVOICES_TAB =
  `${SURFOPS_ORDERS_INVOICES}/tab/:statusTab` as const;
export const SURFOPS_ORDERS_INVOICES_CREATE_DRAFTS =
  `${SURFOPS_ORDERS_INVOICES}/create-drafts` as const;
export const SURFOPS_ORDERS_INVOICE_DETAIL =
  `${SURFOPS_ORDERS_INVOICES}/:invoiceId` as const;

/**
 * Helper object containing functions to generate paths for different routes
 *
 * Tenant-scoped helpers default to the active tenant in the current URL when
 * no `tenantSlug` is passed; pass a slug explicitly to generate cross-tenant
 * links (e.g. when navigating during a tenant switch).
 *
 * @namespace getPath
 */
export const getPath = {
  /**
   * Tenant-prefixed root URL for the active (or given) tenant (`/~/…`). Unlike
   * the bare `ROOT` (`/`) constant, preserves the active tenant slug from the
   * current URL when `tenantSlug` is omitted. Use for "Back to App" and other
   * exits from admin or SurfOps where dropping the tenant segment would switch
   * tenants.
   */
  tenantRoot: (tenantSlug?: string) =>
    generatePath(TENANT_ROOT, { tenantSlug: resolveTenantSlug(tenantSlug) }),

  /** `<Link>`/`navigate`-ready URL for the Ask landing page. */
  ask: (tenantSlug?: string) =>
    generatePath(ASK, { tenantSlug: resolveTenantSlug(tenantSlug) }),

  /** `<Link>`/`navigate`-ready URL for the Documents area. */
  documents: (tenantSlug?: string) =>
    generatePath(DOCUMENTS, { tenantSlug: resolveTenantSlug(tenantSlug) }),

  /** `<Link>`/`navigate`-ready URL for Document Management. */
  documentManagement: (tenantSlug?: string) =>
    generatePath(DOCUMENT_MANAGEMENT, {
      tenantSlug: resolveTenantSlug(tenantSlug),
    }),

  /** `<Link>`/`navigate`-ready URL for starting a new Document Management flow. */
  documentManagementNew: (tenantSlug?: string) =>
    generatePath(DOCUMENT_MANAGEMENT_NEW, {
      tenantSlug: resolveTenantSlug(tenantSlug),
    }),

  /** `<Link>`/`navigate`-ready URL for Issues Reported list. */
  issuesReportedList: (tenantSlug?: string) =>
    generatePath(ISSUES_REPORTED, {
      tenantSlug: resolveTenantSlug(tenantSlug),
    }),

  /** `<Link>`/`navigate`-ready URL for Delinquencies. */
  delinquencies: (tenantSlug?: string) =>
    generatePath(DELINQUENCIES, { tenantSlug: resolveTenantSlug(tenantSlug) }),

  /**
   * Generates path for dialogue details
   * @param {string} dialogueId - ID of the dialogue
   * @returns {string} Generated path for dialogue details
   */
  dialogueDetails: (dialogueId: string, tenantSlug?: string) =>
    generatePath(DIALOGUE_DETAILS, {
      tenantSlug: resolveTenantSlug(tenantSlug),
      dialogueId,
    }),

  /**
   * Generates path for priorities view
   * @param {string} [taskType] - Optional task type filter
   * @returns {string} Generated path for priorities view
   */
  priorities: (taskType?: string, tenantSlug?: string) => {
    const base = generatePath(PRIORITIES, {
      tenantSlug: resolveTenantSlug(tenantSlug),
    });
    return taskType ? `${base}/${taskType}` : base;
  },

  /**
   * Generates path for delinquency details
   * @param {string} propertyId - ID of the property
   * @param {string} delinquencyId - ID of the delinquency
   * @returns {string} Generated path for delinquency details
   */
  delinquency: (
    propertyId: string,
    delinquencyId: string,
    tenantSlug?: string
  ) =>
    generatePath(DELINQUENCY, {
      tenantSlug: resolveTenantSlug(tenantSlug),
      propertyId,
      delinquencyId,
    }),

  /**
   * Object containing path generators for issues reported routes (customer view)
   */
  issuesReported: {
    /**
     * Generates path for issue detail page
     * @param {string} propertyId - ID of the property
     * @param {string} issueId - ID of the issue
     * @returns {string} Generated path for issue details
     */
    detail: (propertyId: string, issueId: string, tenantSlug?: string) =>
      generatePath(ISSUES_REPORTED_DETAIL, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
        issueId,
      }),
  },

  /**
   * Generates path for document management (lease migration) details
   * @param propertyId - ID of the property
   * @param migrationId - ID of the lease migration
   * @returns Generated path for document management details
   */
  documentManagementDetail: (
    propertyId: string,
    migrationId: string,
    tenantSlug?: string
  ) =>
    generatePath(DOCUMENT_MANAGEMENT_DETAIL, {
      tenantSlug: resolveTenantSlug(tenantSlug),
      propertyId,
      id: migrationId,
    }),

  /**
   * Generates path for document management (lease migration) upload documents page
   */
  documentManagementUpload: (
    propertyId: string,
    migrationId: string,
    tenantSlug?: string
  ) =>
    generatePath(DOCUMENT_MANAGEMENT_UPLOAD, {
      tenantSlug: resolveTenantSlug(tenantSlug),
      propertyId,
      id: migrationId,
    }),

  /**
   * Generates path for document management (lease migration) processing page
   */
  documentManagementProcessing: (
    propertyId: string,
    migrationId: string,
    tenantSlug?: string
  ) =>
    generatePath(DOCUMENT_MANAGEMENT_PROCESSING, {
      tenantSlug: resolveTenantSlug(tenantSlug),
      propertyId,
      id: migrationId,
    }),

  /**
   * Generates path for document management (lease migration) review page
   */
  documentManagementReview: (
    propertyId: string,
    migrationId: string,
    tenantSlug?: string
  ) =>
    generatePath(DOCUMENT_MANAGEMENT_REVIEW, {
      tenantSlug: resolveTenantSlug(tenantSlug),
      propertyId,
      id: migrationId,
    }),

  /**
   * Path helpers for property transitions (acquisition / disposition).
   */
  propertyTransitions: {
    list: (tenantSlug?: string) =>
      generatePath(PROPERTY_TRANSITIONS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),
    create: (tenantSlug?: string) =>
      generatePath(PROPERTY_TRANSITIONS_CREATE, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),
    templates: (tenantSlug?: string) =>
      generatePath(PROPERTY_TRANSITIONS_TEMPLATES, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),
    detail: (transitionId: string, tenantSlug?: string) =>
      generatePath(PROPERTY_TRANSITIONS_DETAIL, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        transitionId,
      }),
    setup: (transitionId: string, tenantSlug?: string) =>
      generatePath(PROPERTY_TRANSITIONS_SETUP, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        transitionId,
      }),
    documentDetail: (
      transitionId: string,
      documentId: string,
      tenantSlug?: string
    ) =>
      generatePath(PROPERTY_TRANSITIONS_DOCUMENT, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        transitionId,
        documentId,
      }),
    taskDetail: (
      transitionId: string,
      transitionTaskId: string,
      tenantSlug?: string
    ) =>
      generatePath(PROPERTY_TRANSITIONS_TASK, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        transitionId,
        transitionTaskId,
      }),
    createTask: (transitionId: string, tenantSlug?: string) =>
      generatePath(PROPERTY_TRANSITIONS_TASK_CREATE, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        transitionId,
      }),
    taskDocument: (
      transitionId: string,
      transitionTaskId: string,
      documentId: string,
      tenantSlug?: string
    ) =>
      generatePath(PROPERTY_TRANSITIONS_TASK_DOCUMENT, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        transitionId,
        transitionTaskId,
        documentId,
      }),
    taskEditDetails: (
      transitionId: string,
      transitionTaskId: string,
      tenantSlug?: string
    ) =>
      generatePath(PROPERTY_TRANSITIONS_TASK_EDIT_DETAILS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        transitionId,
        transitionTaskId,
      }),
    taskEditDocumentRequirements: (
      transitionId: string,
      transitionTaskId: string,
      tenantSlug?: string
    ) =>
      generatePath(PROPERTY_TRANSITIONS_TASK_EDIT_DOCUMENT_REQUIREMENTS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        transitionId,
        transitionTaskId,
      }),
    transitionVariables: (transitionId: string, tenantSlug?: string) =>
      generatePath(PROPERTY_TRANSITIONS_DETAIL_VARIABLES, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        transitionId,
      }),
    collections: (transitionId: string, tenantSlug?: string) =>
      generatePath(PROPERTY_TRANSITIONS_COLLECTIONS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        transitionId,
      }),
    collectionDetail: (
      transitionId: string,
      collectionId: string,
      tenantSlug?: string
    ) =>
      generatePath(PROPERTY_TRANSITIONS_COLLECTION_DETAIL, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        transitionId,
        collectionId,
      }),
    companies: (tenantSlug?: string) =>
      generatePath(PROPERTY_TRANSITIONS_COMPANIES, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),
    company: (companyId: string, tenantSlug?: string) =>
      generatePath(PROPERTY_TRANSITIONS_COMPANY_DETAIL, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        companyId,
      }),
    companyTeam: (companyId: string, team: string, tenantSlug?: string) =>
      // Team names are user-provided free-form strings that can contain
      // spaces / punctuation; ``generatePath`` does not URL-encode segments,
      // so we encode here to keep the ``:team`` path segment valid.
      generatePath(PROPERTY_TRANSITIONS_COMPANY_TEAM, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        companyId,
        team: encodeURIComponent(team),
      }),
  },

  /**
   * Object containing path generators for lease audit routes
   */
  leaseAuditHotSpot: {
    detail: (checkType: string, failureType: string, tenantSlug?: string) =>
      generatePath(LEASE_AUDIT_HOT_SPOT_DETAIL, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        checkType,
        failureType,
      }),
    checkExplorer: (
      checkType: string,
      failureType: string,
      propertyId: string,
      auditId: string,
      auditRunId: string,
      tenantSlug?: string
    ) =>
      generatePath(LEASE_AUDIT_HOT_SPOT_CHECK_EXPLORER, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        checkType,
        failureType,
        propertyId,
        auditId,
        auditRunId,
      }),
  },

  leaseAudit: {
    /**
     * Generates path for property lease audits list
     */
    list: (tenantSlug?: string) =>
      generatePath(LEASE_AUDITS, { tenantSlug: resolveTenantSlug(tenantSlug) }),

    /**
     * Generates path for lease audit details
     */
    details: (propertyId: string, auditId: string, tenantSlug?: string) =>
      generatePath(LEASE_AUDIT, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
        auditId,
      }),

    /**
     * Generates path for specific lease audit run
     */
    run: (
      propertyId: string,
      auditId: string,
      auditRunId: string,
      tenantSlug?: string
    ) =>
      generatePath(LEASE_AUDIT_RUN, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
        auditId,
        auditRunId,
      }),

    /**
     * Generates path for lease audit runs list
     */
    runs: (propertyId: string, auditId: string, tenantSlug?: string) =>
      generatePath(LEASE_AUDIT_RUNS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
        auditId,
      }),

    /**
     * Generates path for lease audit check explorer
     */
    checkExplorer: (
      propertyId: string,
      auditId: string,
      auditRunId: string,
      checkType: string,
      tenantSlug?: string
    ) =>
      generatePath(LEASE_AUDIT_CHECK_EXPLORER, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
        auditId,
        auditRunId,
        checkType,
      }),
  },

  dueDiligence: {
    /**
     * Generates path for the due diligence landing list
     * @returns {string} Generated path for due diligence list
     */
    list: (tenantSlug?: string) =>
      generatePath(DUE_DILIGENCES, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),

    /**
     * Generates path for the due diligence report/dashboard
     * @returns {string} Generated path for due diligence report
     */
    report: (tenantSlug?: string) =>
      generatePath(DUE_DILIGENCE_REPORT, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),

    /**
     * Generates path for the standalone PDF-ready due diligence report view.
     *
     * Renders one or more report sections as 8.5"x11" portrait pages with no
     * app chrome (no sidebar, topbar, or user menu) so the page can be cleanly
     * printed to PDF. When `sectionIds` is omitted all sections are rendered;
     * pass an explicit list (in render order) to filter and reorder sections.
     *
     * @param propertyId - Property the report is for
     * @param exportId - Property file export id that points at the
     *   `DDReportData` JSON in S3 (passed to
     *   `get_due_diligence_report_data(property_id, report_data_id)`)
     * @param sectionIds - Optional list of section ids to render
     */
    pdfView: (
      propertyId: string,
      exportId: string,
      sectionIds?: string[],
      tenantSlug?: string
    ) => {
      const params = new URLSearchParams();
      params.set('property_id', propertyId);
      params.set('export_id', exportId);
      sectionIds?.forEach((id) => params.append('section_id', id));
      const base = generatePath(DUE_DILIGENCE_PDF_VIEW, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      });
      return `${base}?${params.toString()}`;
    },

    /**
     * Generates path for creating a new due diligence property
     * @returns {string} Generated path for new due diligence property
     */
    new: (tenantSlug?: string) =>
      generatePath(DUE_DILIGENCE_NEW, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),

    /**
     * Generates path for the admin-only DD reporting settings page (currently
     * hosts the LLM prompt-guideline overrides; the name is deliberately
     * broader so future report-level toggles can share the URL).
     *
     * The saved settings are keyed server-side by `saas_tenant_id`, so the
     * URL is not tenant-parameterized beyond the standard `/~/:tenantSlug`
     * root — the tenant context is already implicit in the URL slug.
     *
     * @returns Generated path for the reporting settings editor
     */
    reportingSettings: (tenantSlug?: string) =>
      generatePath(DUE_DILIGENCE_REPORTING_SETTINGS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),

    /**
     * Generates path for a property's due diligence details in the property context
     */
    property: (propertyId: string, tenantSlug?: string) =>
      generatePath(DUE_DILIGENCE, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),
    audit: (propertyId: string, auditId: string, tenantSlug?: string) =>
      generatePath(DUE_DILIGENCE_AUDIT, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
        auditId,
      }),
    auditRuns: (propertyId: string, auditId: string, tenantSlug?: string) =>
      generatePath(DUE_DILIGENCE_AUDIT_RUNS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
        auditId,
      }),
    auditRun: (
      propertyId: string,
      auditId: string,
      auditRunId: string,
      tenantSlug?: string
    ) =>
      generatePath(DUE_DILIGENCE_AUDIT_RUN, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
        auditId,
        auditRunId,
      }),
    checkExplorer: (
      propertyId: string,
      auditId: string,
      auditRunId: string,
      checkType: string,
      tenantSlug?: string
    ) =>
      generatePath(DUE_DILIGENCE_AUDIT_CHECK_EXPLORER, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
        auditId,
        auditRunId,
        checkType,
      }),
  },

  /**
   * Object containing path generators for due diligence property workflows
   * (migrated from admin prospective routes)
   */
  dueDiligenceAdmin: {
    propertyDetails: (propertyId: string, tenantSlug?: string) =>
      generatePath(DUE_DILIGENCE_PROPERTY, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),
    /** Path for the gate that redirects to the first incomplete step or report when complete. */
    propertyLatest: (propertyId: string, tenantSlug?: string) =>
      generatePath(DUE_DILIGENCE_PROPERTY_LATEST, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),
    propertySetup: (propertyId: string, tenantSlug?: string) =>
      generatePath(DUE_DILIGENCE_PROPERTY_SETUP, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),
    propertyConfigureChecks: (propertyId: string, tenantSlug?: string) =>
      generatePath(DUE_DILIGENCE_PROPERTY_CONFIGURE_CHECKS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),
    propertyDocuments: (propertyId: string, tenantSlug?: string) =>
      generatePath(DUE_DILIGENCE_PROPERTY_DOCUMENTS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),
    propertyLeaseSummary: (propertyId: string, tenantSlug?: string) =>
      generatePath(DUE_DILIGENCE_PROPERTY_LEASE_SUMMARY, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),
    propertyDocumentClassification: (propertyId: string, tenantSlug?: string) =>
      generatePath(DUE_DILIGENCE_PROPERTY_DOCUMENT_CLASSIFICATION, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),
    propertyChargeCodes: (propertyId: string, tenantSlug?: string) =>
      generatePath(DUE_DILIGENCE_PROPERTY_CHARGE_CODES, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),
    propertyProcessing: (propertyId: string, tenantSlug?: string) =>
      generatePath(DUE_DILIGENCE_PROPERTY_PROCESSING, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),
  },

  /**
   * Object containing path generators for Settings admin routes
   * (`/~/:tenantSlug/admin/...`).
   */
  admin: {
    /**
     * Generates path to the admin root (index redirects to properties).
     */
    root: (tenantSlug?: string) =>
      generatePath(ADMIN, { tenantSlug: resolveTenantSlug(tenantSlug) }),

    /**
     * Generates path to admin properties page
     */
    properties: (tenantSlug?: string) =>
      generatePath(ADMIN_PROPERTIES, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),

    /**
     * Generates path to create new property page
     */
    newProperty: (tenantSlug?: string) =>
      generatePath(ADMIN_PROPERTIES_NEW, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),

    /**
     * Generates path to property detail page in admin context
     */
    propertyDetails: (propertyId: string, tenantSlug?: string) =>
      generatePath(ADMIN_PROSPECTIVE_PROPERTY, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),

    /**
     * Generates path to property setup page in admin context
     */
    propertySetup: (propertyId: string, tenantSlug?: string) =>
      generatePath(ADMIN_PROSPECTIVE_PROPERTY_SETUP, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),

    /**
     * Generates path to property configure checks page in admin context
     */
    propertyConfigureChecks: (propertyId: string, tenantSlug?: string) =>
      generatePath(ADMIN_PROSPECTIVE_PROPERTY_CONFIGURE_CHECKS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),

    /**
     * Generates path to property documents page in admin context
     */
    propertyDocuments: (propertyId: string, tenantSlug?: string) =>
      generatePath(ADMIN_PROSPECTIVE_PROPERTY_DOCUMENTS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),

    /**
     * Generates path to property lease summary page in admin context
     */
    propertyLeaseSummary: (propertyId: string, tenantSlug?: string) =>
      generatePath(ADMIN_PROSPECTIVE_PROPERTY_LEASE_SUMMARY, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),

    /**
     * Generates path to property document classification page in admin context
     */
    propertyDocumentClassification: (propertyId: string, tenantSlug?: string) =>
      generatePath(ADMIN_PROSPECTIVE_PROPERTY_DOCUMENT_CLASSIFICATION, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),

    /**
     * Generates path to property charge codes page in admin context
     */
    propertyChargeCodes: (propertyId: string, tenantSlug?: string) =>
      generatePath(ADMIN_PROSPECTIVE_PROPERTY_CHARGE_CODES, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),

    /**
     * Generates path to managed property charge codes page in admin context
     */
    managedPropertyChargeCodes: (propertyId: string, tenantSlug?: string) =>
      generatePath(ADMIN_PROPERTY_MANAGED_CHARGE_CODES, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),

    /**
     * Generates path to managed property checks page in admin context
     */
    managedPropertyChecks: (propertyId: string, tenantSlug?: string) =>
      generatePath(ADMIN_PROPERTY_MANAGED_CHECKS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),

    /**
     * Generates path to property processing page in admin context
     */
    propertyProcessing: (propertyId: string, tenantSlug?: string) =>
      generatePath(ADMIN_PROSPECTIVE_PROPERTY_PROCESSING, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
      }),

    /**
     * Generates path to admin users page
     */
    users: (tenantSlug?: string) =>
      generatePath(ADMIN_USERS, { tenantSlug: resolveTenantSlug(tenantSlug) }),

    /**
     * Generates path to admin tenants page
     */
    tenants: (tenantSlug?: string) =>
      generatePath(ADMIN_TENANTS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),
  },

  help: (tenantSlug?: string) =>
    generatePath(HELP, { tenantSlug: resolveTenantSlug(tenantSlug) }),

  /** `<Link>`/`navigate`-ready URL for the What's New archive. */
  whatsNew: () => WHATS_NEW,

  /**
   * Generates path for lease audits help page
   * @returns {string} Generated path for lease audits help page
   */
  helpLeaseAudits: (tenantSlug?: string) =>
    generatePath(HELP_LEASE_AUDITS, {
      tenantSlug: resolveTenantSlug(tenantSlug),
    }),

  /**
   * Object containing path generators for document routes
   */
  document: {
    /**
     * Generates path for document details
     * @param {string} propertyId - ID of the property
     * @param {string} documentId - ID of the document
     * @returns {string} Generated path for document details
     */
    details: (propertyId: string, documentId: string, tenantSlug?: string) =>
      generatePath(DOCUMENT_DETAILS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
        documentId,
      }),

    /**
     * Generates path for document profile editor
     * @param {string} propertyId - ID of the property
     * @param {string} documentId - ID of the document
     * @returns {string} Generated path for document profile editor
     */
    profileEditor: (
      propertyId: string,
      documentId: string,
      tenantSlug?: string
    ) =>
      generatePath(DOCUMENT_PROFILE_EDITOR, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
        documentId,
      }),

    /**
     * Generates path for document extract editor
     * @param {string} propertyId - ID of the property
     * @param {string} documentId - ID of the document
     * @returns {string} Generated path for document extract editor
     */
    extractEditor: (
      propertyId: string,
      documentId: string,
      tenantSlug?: string
    ) =>
      generatePath(DOCUMENT_EXTRACT_EDITOR, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
        documentId,
      }),

    /**
     * Generates path for document index editor
     * @param {string} propertyId - ID of the property
     * @param {string} documentId - ID of the document
     * @returns {string} Generated path for document index editor
     */
    indexEditor: (
      propertyId: string,
      documentId: string,
      tenantSlug?: string
    ) =>
      generatePath(DOCUMENT_INDEX_EDITOR, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
        documentId,
      }),
  },

  /**
   * Object containing path generators for lease-scoped routes
   */
  lease: {
    /**
     * Generates path for the documents list scoped to a single lease.
     * @param propertyId - ID of the property
     * @param leaseId - ID of the lease
     * @returns Generated path for the lease documents page
     */
    documents: (propertyId: string, leaseId: string, tenantSlug?: string) =>
      generatePath(LEASE_DOCUMENTS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
        leaseId,
      }),
  },

  /**
   * Object containing path generators for SurfOps routes
   *
   * SurfOps routes live under `/~/:tenantSlug/surfops` so the URL tenant drives
   * the `saas-tenant-id` header and sidebar tenant combobox.
   */
  surfops: {
    root: (tenantSlug?: string) =>
      generatePath(SURFOPS, { tenantSlug: resolveTenantSlug(tenantSlug) }),

    /**
     * Generates path for config management
     */
    configManagement: (tenantSlug?: string) =>
      generatePath(SURFOPS_CONFIG_MANAGEMENT, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),

    configCreate: (tenantSlug?: string) =>
      generatePath(SURFOPS_CONFIG_CREATE, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),

    configDetail: (
      propertyId: string,
      configType: string,
      tenantSlug?: string
    ) =>
      generatePath(SURFOPS_CONFIG_DETAIL, {
        tenantSlug: resolveTenantSlug(tenantSlug),
        propertyId,
        configType,
      }),

    configHistoryManagement: (tenantSlug?: string) =>
      generatePath(SURFOPS_CONFIG_HISTORY_MANAGEMENT, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),

    operationalIntegrity: (tenantSlug?: string) =>
      generatePath(SURFOPS_OPERATIONAL_INTEGRITY, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),

    unpaidLicensedFeatures: (tenantSlug?: string) =>
      generatePath(SURFOPS_UNPAID_LICENSED_FEATURES, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),

    experiments: {
      list: (tenantSlug?: string) =>
        generatePath(SURFOPS_EXPERIMENTS, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      create: (tenantSlug?: string) =>
        generatePath(SURFOPS_EXPERIMENTS_CREATE, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      documents: (tenantSlug?: string) =>
        generatePath(SURFOPS_EXPERIMENTS_DOCUMENTS, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      detail: (experimentId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_EXPERIMENT, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          experimentId,
        }),
      run: (experimentId: string, runId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_EXPERIMENT_RUN, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          experimentId,
          runId,
        }),
    },

    leaseAuditBatches: {
      list: (tenantSlug?: string) =>
        generatePath(SURFOPS_LEASE_AUDIT_BATCHES, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      create: (tenantSlug?: string) =>
        generatePath(SURFOPS_LEASE_AUDIT_BATCH_CREATE, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      detail: (runId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_LEASE_AUDIT_BATCH_DETAILS, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          runId,
        }),
    },

    leaseAuditMedic: (tenantSlug?: string) =>
      generatePath(SURFOPS_LEASE_AUDIT_MEDIC, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),

    leaseAuditPropertyOnboarding: {
      list: (tenantSlug?: string) =>
        generatePath(SURFOPS_LEASE_AUDIT_PROPERTY_ONBOARDING, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      detail: (runId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_LEASE_AUDIT_PROPERTY_ONBOARDING_DETAIL, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          runId,
        }),
    },

    trialAudits: {
      list: (tenantSlug?: string) =>
        generatePath(SURFOPS_TRIAL_AUDITS, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      detail: (batchId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_TRIAL_AUDIT_BATCH, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          batchId,
        }),
      check: (batchId: string, checkId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_TRIAL_AUDIT_CHECK, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          batchId,
          checkId,
        }),
    },

    eval: {
      list: (tenantSlug?: string) =>
        generatePath(SURFOPS_EVAL, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      create: (tenantSlug?: string) =>
        generatePath(SURFOPS_EVAL_CREATE, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      detail: (datasetId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_EVAL_DATASET, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          datasetId,
        }),
      label: (datasetId: string, itemId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_EVAL_LABEL, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          datasetId,
          itemId,
        }),
      run: (datasetId: string, runId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_EVAL_RUN, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          datasetId,
          runId,
        }),
      compare: (datasetId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_EVAL_COMPARE, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          datasetId,
        }),
    },

    extractionQuality: {
      list: (tenantSlug?: string) =>
        generatePath(SURFOPS_EXTRACTION_QUALITY, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      cluster: (archetypeId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_EXTRACTION_QUALITY_CLUSTER, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          archetypeId,
        }),
      detail: (findingId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_EXTRACTION_QUALITY_FINDING, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          findingId,
        }),
    },

    issuesReported: {
      list: (tenantSlug?: string) =>
        generatePath(SURFOPS_ISSUES_REPORTED, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      detail: (propertyId: string, issueId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_ISSUES_REPORTED_DETAILS, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          propertyId,
          issueId,
        }),
    },
    apiKeys: (tenantSlug?: string) =>
      generatePath(SURFOPS_API_KEYS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),
    tenants: {
      list: (tenantSlug?: string) =>
        generatePath(SURFOPS_TENANTS, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      create: (tenantSlug?: string) =>
        generatePath(SURFOPS_TENANTS_CREATE, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      detail: (surfopsTenantId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_TENANT, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          surfopsTenantId,
        }),
    },
    properties: {
      list: (tenantSlug?: string) =>
        generatePath(SURFOPS_PROPERTIES, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      detail: (propertyId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_PROPERTY, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          propertyId,
        }),
    },
    policyDocuments: (tenantSlug?: string) =>
      generatePath(SURFOPS_POLICY_DOCUMENTS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),
    cloudSync: (tenantSlug?: string) =>
      generatePath(SURFOPS_CLOUDSYNC, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),
    documentContentSync: (tenantSlug?: string) =>
      generatePath(SURFOPS_DOCUMENT_CONTENT_SYNC, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),
    documentReindex: (tenantSlug?: string) =>
      generatePath(SURFOPS_DOCUMENT_REINDEX, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),
    agentChat: (tenantSlug?: string) =>
      generatePath(SURFOPS_AGENT_CHAT, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),
    pipelines: {
      list: (tenantSlug?: string) =>
        generatePath(SURFOPS_PIPELINES, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      detail: (pipelineDirectiveId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_PIPELINE_RUN, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          pipelineDirectiveId,
        }),
    },
    externalJobs: {
      list: (tenantSlug?: string) =>
        generatePath(SURFOPS_EXTERNAL_JOBS, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      detail: (externalJobId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_EXTERNAL_JOB, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          externalJobId,
        }),
      byDocument: (documentId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_EXTERNAL_JOB_DOCUMENT, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          documentId,
        }),
    },
    emailOutbox: {
      list: (tenantSlug?: string) =>
        generatePath(SURFOPS_EMAIL_OUTBOX, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
    },
    emailSync: {
      list: (tenantSlug?: string) =>
        generatePath(SURFOPS_EMAIL_SYNC, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      userDetail: (surfaceUserId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_EMAIL_SYNC_USER_DETAIL, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          surfaceUserId,
        }),
      poisonPills: (tenantSlug?: string) =>
        generatePath(SURFOPS_EMAIL_SYNC_POISON_PILLS, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
    },

    design: (tenantSlug?: string) =>
      generatePath(SURFOPS_DESIGN, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),

    components: (tenantSlug?: string) =>
      generatePath(SURFOPS_COMPONENTS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),

    notifications: (tenantSlug?: string) =>
      generatePath(SURFOPS_NOTIFICATIONS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),
    transitionAgentSettings: (tenantSlug?: string) =>
      generatePath(SURFOPS_TRANSITION_AGENT_SETTINGS, {
        tenantSlug: resolveTenantSlug(tenantSlug),
      }),

    classificationSetup: {
      list: (tenantSlug?: string) =>
        generatePath(SURFOPS_CLASSIFICATION_SETUP, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      // ``classification`` is the enum display value (e.g. "Rent Roll Report").
      // ``generatePath`` percent-encodes segment params, so callers should
      // pass the raw value.
      detail: (classification: string, tenantSlug?: string) =>
        generatePath(SURFOPS_CLASSIFICATION_SETUP_DETAIL, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          classification,
        }),
    },

    transitions: {
      fileUploads: (tenantSlug?: string) =>
        generatePath(SURFOPS_TRANSITIONS_FILE_UPLOADS, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      fileUploadDetail: (fileUploadId: string, tenantSlug?: string) =>
        generatePath(SURFOPS_TRANSITIONS_FILE_UPLOAD_DETAIL, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          fileUploadId,
        }),
      documents: (tenantSlug?: string) =>
        generatePath(SURFOPS_TRANSITIONS_DOCUMENTS, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      documentDetail: (
        transitionId: string,
        documentId: string,
        tenantSlug?: string
      ) =>
        generatePath(SURFOPS_TRANSITIONS_DOCUMENT_DETAIL, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          transitionId,
          documentId,
        }),
      documentIndexEditor: (
        transitionId: string,
        documentId: string,
        tenantSlug?: string
      ) =>
        generatePath(SURFOPS_TRANSITIONS_DOCUMENT_INDEX_EDITOR, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          transitionId,
          documentId,
        }),
      documentExtractEditor: (
        transitionId: string,
        documentId: string,
        tenantSlug?: string
      ) =>
        generatePath(SURFOPS_TRANSITIONS_DOCUMENT_EXTRACT_EDITOR, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          transitionId,
          documentId,
        }),
      documentProfileEditor: (
        transitionId: string,
        documentId: string,
        tenantSlug?: string
      ) =>
        generatePath(SURFOPS_TRANSITIONS_DOCUMENT_PROFILE_EDITOR, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          transitionId,
          documentId,
        }),
    },
    orders: {
      list: (tenantSlug?: string) =>
        generatePath(SURFOPS_ORDERS, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      detail: (orderId: number | string, tenantSlug?: string) =>
        generatePath(SURFOPS_ORDER_DETAIL, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          orderId: String(orderId),
        }),
      bulkAddItems: (orderId: number | string, tenantSlug?: string) =>
        generatePath(SURFOPS_ORDER_BULK_ADD_ITEMS, {
          tenantSlug: resolveTenantSlug(tenantSlug),
          orderId: String(orderId),
        }),
      outstanding: (tenantSlug?: string) =>
        generatePath(SURFOPS_ORDERS_OUTSTANDING, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      propertySearch: (tenantSlug?: string) =>
        generatePath(SURFOPS_ORDERS_PROPERTY_SEARCH, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      reports: (tenantSlug?: string) =>
        generatePath(SURFOPS_ORDERS_REPORTS, {
          tenantSlug: resolveTenantSlug(tenantSlug),
        }),
      customers: {
        list: (tenantSlug?: string) =>
          generatePath(SURFOPS_ORDERS_CUSTOMERS, {
            tenantSlug: resolveTenantSlug(tenantSlug),
          }),
        new: (tenantSlug?: string) =>
          generatePath(SURFOPS_ORDERS_CUSTOMER_NEW, {
            tenantSlug: resolveTenantSlug(tenantSlug),
          }),
        detail: (customerId: number | string, tenantSlug?: string) =>
          generatePath(SURFOPS_ORDERS_CUSTOMER_DETAIL, {
            tenantSlug: resolveTenantSlug(tenantSlug),
            customerId: String(customerId),
          }),
      },
      prepurchases: {
        list: (tenantSlug?: string) =>
          generatePath(SURFOPS_ORDERS_PREPURCHASES, {
            tenantSlug: resolveTenantSlug(tenantSlug),
          }),
        detail: (ledgerId: string, tenantSlug?: string) =>
          generatePath(SURFOPS_ORDERS_PREPURCHASE_DETAIL, {
            tenantSlug: resolveTenantSlug(tenantSlug),
            ledgerId,
          }),
      },
      invoices: {
        list: (tenantSlug?: string) =>
          generatePath(SURFOPS_ORDERS_INVOICES, {
            tenantSlug: resolveTenantSlug(tenantSlug),
          }),
        tab: (statusTab: 'draft' | 'finalized' | 'void', tenantSlug?: string) =>
          generatePath(SURFOPS_ORDERS_INVOICES_TAB, {
            tenantSlug: resolveTenantSlug(tenantSlug),
            statusTab,
          }),
        createDrafts: (tenantSlug?: string) =>
          generatePath(SURFOPS_ORDERS_INVOICES_CREATE_DRAFTS, {
            tenantSlug: resolveTenantSlug(tenantSlug),
          }),
        detail: (invoiceId: string, tenantSlug?: string) =>
          generatePath(SURFOPS_ORDERS_INVOICE_DETAIL, {
            tenantSlug: resolveTenantSlug(tenantSlug),
            invoiceId,
          }),
      },
    },
  },

  /**
   * Object containing path generators for API routes
   */
  api: {
    /**
     * Generates URL for PMS Portal
     * @returns {string} Full URL for PMS Portal
     */
    pmsPortal: () => '/api/pms-portal',
    /**
     * Generates URL for Orders Portal
     * @returns {string} Full URL for Orders Portal
     */
    ordersPortal: () => '/api/orders-portal',
    /**
     * Generates URL for Job Queue
     * @returns {string} Full URL for Job Queue
     */
    jobQueue: () => '/api/job-queue',

    /**
     * Generates URL for LLM Costs
     * @returns {string} Full URL for LLM Costs
     */
    llmCosts: () => '/api/surfops/llm-costs',

    /**
     * Generates URL for Lease Audit Discovery
     * @param {string} tenantId - ID of the tenant
     * @returns {string} Full URL for Lease Audit Discovery
     */
    leaseAuditDiscovery: (tenantId: string) =>
      `/api/lease-audits/discovery/tenants/${tenantId}/checks-by-property`,

    /**
     * Generates URL for Auditing Shadow Results
     * @returns {string} Full URL for Auditing Shadow Results
     */
    auditingShadowResults: () => '/api/surfops/auditing-shadow/results',

    /**
     * Generates URL for Auditing Disallow Results
     * @returns {string} Full URL for Auditing Disallow Results
     */
    auditingDisallowResults: () => '/api/surfops/auditing-disallow/results',

    /**
     * Generates URL for Audit Config Reporting
     * @returns {string} Full URL for Audit Config Reporting
     */
    auditConfigReporting: () =>
      '/api/surfops/auditing-reporting/audit-config/results',

    /**
     * Generates URL for PMS Fetched Payloads
     * @returns {string} Full URL for PMS Fetched Payloads
     */
    pmsFetchedPayloads: () => '/api/pmsfetchingest/fetched-payloads',

    /**
     * Generates URL for HappyCo Business Partners
     * @returns {string} Full URL for HappyCo Business Partners
     */
    happycoBusinessPartners: () => '/api/business-partners/happyco',

    /**
     * Generates URL for Okta Results
     * @returns {string} Full URL for Okta Results
     */
    oktaResults: () => '/api/surfops/okta/results',
  },
};
